import { useState } from 'react';
import api, { descargarReporte } from '../api/client.js';
import { useToast } from '../context/ToastContext.jsx';
import { hoy, fechaHora } from '../utils/format.js';
import { PageHeader, Modal, Loading } from '../components/UI.jsx';
import { Icon } from '../components/Icons.jsx';

const REPORTES = [
  { id: 'ventas', titulo: 'Ventas', desc: 'Detalle de ventas con subtotal, IVA y total sumados al final', conFechas: true, icon: 'ventas' },
  { id: 'iva', titulo: 'Resumen de IVA (por dia)', desc: 'Base gravada e IVA cobrado por dia, con totales del periodo', conFechas: true, icon: 'reportes' },
  { id: 'iva-periodo', titulo: 'Declaracion IVA (periodo)', desc: 'Como OVI: ventas y compras/gastos por tarifa, IVA por pagar', conFechas: true, icon: 'reportes' },
  { id: 'libro-compras', titulo: 'Libro de compras', desc: 'Comprobantes de proveedores con gravado, exento, IVA y totales', conFechas: true, icon: 'recibidos' },
  { id: 'inventario', titulo: 'Inventario', desc: 'Existencias, costos y valor total del inventario', conFechas: false, icon: 'inventario' },
  { id: 'gastos', titulo: 'Gastos', desc: 'Detalle con base e IVA separados, por categoria y acreditable', conFechas: true, icon: 'gastos' },
  { id: 'compras', titulo: 'Compras', desc: 'Compras a proveedores con desglose de IVA por tarifa', conFechas: true, icon: 'compras' },
];

function rutaReporte(rep, desde, hasta, extra = '') {
  let ruta = `/reportes/${rep.id}`;
  const qs = [];
  if (rep.conFechas && desde) qs.push(`desde=${desde}`);
  if (rep.conFechas && hasta) qs.push(`hasta=${hasta}`);
  if (extra) qs.push(extra);
  if (qs.length) ruta += '?' + qs.join('&');
  return ruta;
}

export default function Reportes() {
  const toast = useToast();
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [bajando, setBajando] = useState('');
  const [preview, setPreview] = useState(null); // { rep, data }
  const [cargandoPreview, setCargandoPreview] = useState(false);

  const descargar = async (rep) => {
    setBajando(rep.id);
    try {
      const ruta = rutaReporte(rep, desde, hasta);
      await descargarReporte(ruta, `${rep.id}_${hoy()}.csv`);
      toast.success(`Reporte de ${rep.titulo.toLowerCase()} descargado`);
    } catch {
      toast.error('No se pudo generar el reporte');
    } finally {
      setBajando('');
    }
  };

  const previsualizar = async (rep) => {
    setCargandoPreview(true);
    setPreview({ rep, data: null });
    try {
      const ruta = rutaReporte(rep, desde, hasta, 'formato=json');
      const { data } = await api.get(ruta);
      setPreview({ rep, data: data.data });
    } catch {
      toast.error('No se pudo cargar la previsualizacion');
      setPreview(null);
    } finally {
      setCargandoPreview(false);
    }
  };

  const cerrarPreview = () => setPreview(null);

  return (
    <div>
      <PageHeader
        title="Descargables"
        subtitle="Vista previa en pantalla o descarga Excel (CSV) para tu contador"
      />

      <div className="card" style={{ padding: 18, marginBottom: 18 }}>
        <div className="flex items-center gap-sm" style={{ marginBottom: 12 }}>
          <Icon.reportes width={18} height={18} />
          <strong>Rango de fechas</strong>
          <span className="muted" style={{ fontSize: 13 }}>
            (aplica a ventas, IVA, gastos, compras y libro)
          </span>
        </div>
        <div className="flex gap-sm" style={{ flexWrap: 'wrap' }}>
          <div className="field" style={{ margin: 0 }}>
            <label>Desde</label>
            <input
              className="input"
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Hasta</label>
            <input
              className="input"
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </div>
          <button
            className="btn btn-outline"
            style={{ alignSelf: 'flex-end' }}
            onClick={() => {
              setDesde('');
              setHasta('');
            }}
          >
            Todo el historial
          </button>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16,
        }}
      >
        {REPORTES.map((rep) => {
          const IconCmp = Icon[rep.icon];
          return (
            <div
              key={rep.id}
              className="card fade-up"
              style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: 'var(--accent-soft)',
                  color: 'var(--accent-texto)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconCmp width={22} height={22} />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 16, marginBottom: 4 }}>{rep.titulo}</h3>
                <p className="muted" style={{ fontSize: 13 }}>{rep.desc}</p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button
                  className="btn btn-primary btn-block"
                  disabled={cargandoPreview && preview?.rep?.id === rep.id}
                  onClick={() => previsualizar(rep)}
                  title="Ver el reporte en pantalla sin descargar"
                >
                  {cargandoPreview && preview?.rep?.id === rep.id ? (
                    <span className="spinner" />
                  ) : (
                    <>
                      <Icon.print width={16} height={16} /> Vista previa
                    </>
                  )}
                </button>
                <button
                  className="btn btn-outline btn-block"
                  disabled={bajando === rep.id}
                  onClick={() => descargar(rep)}
                  title="Descargar archivo CSV para Excel"
                >
                  {bajando === rep.id ? (
                    <span className="spinner" />
                  ) : (
                    <>
                      <Icon.download width={16} height={16} /> Descargar Excel
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal de previsualizacion */}
      <Modal
        open={!!preview}
        onClose={cerrarPreview}
        title={preview?.rep ? `Vista previa: ${preview.rep.titulo}` : 'Vista previa'}
        width={960}
      >
        {cargandoPreview || !preview?.data ? (
          <Loading texto="Generando previsualizacion…" />
        ) : (
          <PreviewTable
            data={preview.data}
            onDescargar={() => {
              descargar(preview.rep);
            }}
            bajando={bajando === preview.rep?.id}
          />
        )}
      </Modal>
    </div>
  );
}

const TOTAL_LABELS = {
  subtotal: 'Subtotal',
  descuento: 'Descuento',
  impuesto: 'IVA',
  iva_monto: 'IVA',
  iva_cobrado: 'IVA cobrado',
  base_gravada: 'Base gravada',
  total_facturado: 'Total facturado',
  total: 'Total',
  monto: 'Total',
  stock: 'Existencias',
  valor_costo: 'Valor inventario',
  facturas: 'Facturas',
  total_gravado: 'Gravado',
  total_exento: 'Exento',
  total_impuesto: 'IVA facturado',
  total_comprobante: 'Total comprobantes',
  monto_iva_acreditar: 'IVA acreditado',
  ventas_subtotal: 'Ventas (base)',
  ventas_iva: 'IVA ventas',
  compras_gastos_subtotal: 'Compras/gastos (base)',
  compras_gastos_iva: 'IVA acreditable',
  iva_por_pagar: 'IVA por pagar',
};

function PreviewTable({ data, onDescargar, bajando }) {
  const { columnas = [], filas = [], total = 0, nombre, totales, resumen } = data || {};

  if (!filas.length) {
    return (
      <div style={{ textAlign: 'center', padding: '30px 10px' }}>
        <p className="muted" style={{ marginBottom: 12 }}>
          No hay filas para este reporte en el rango seleccionado.
        </p>
        <button className="btn btn-outline" onClick={onDescargar} disabled={bajando}>
          Descargar igual (archivo vacio)
        </button>
      </div>
    );
  }

  const chips = totales
    ? Object.entries(totales).filter(([, v]) => v !== '' && v != null)
    : [];

  return (
    <div>
      <div
        className="flex items-center justify-between"
        style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}
      >
        <div className="muted" style={{ fontSize: 13 }}>
          <strong>{total}</strong> fila(s)
          {nombre ? (
            <span className="mono" style={{ marginLeft: 8, fontSize: 12 }}>
              {nombre}
            </span>
          ) : null}
        </div>
        <button className="btn btn-primary btn-sm" onClick={onDescargar} disabled={bajando}>
          {bajando ? <span className="spinner" /> : (
            <>
              <Icon.download width={15} height={15} /> Descargar Excel
            </>
          )}
        </button>
      </div>

      {chips.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            marginBottom: 12,
          }}
        >
          {chips.map(([key, val]) => (
            <div
              key={key}
              style={{
                background: key.includes('pagar') || key === 'iva_por_pagar'
                  ? 'var(--accent-soft)'
                  : 'var(--surface-2, var(--accent-soft))',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '8px 12px',
                minWidth: 120,
              }}
            >
              <div className="muted" style={{ fontSize: 11, marginBottom: 2 }}>
                {TOTAL_LABELS[key] || key}
              </div>
              <div className="mono" style={{ fontWeight: 700, fontSize: 15 }}>
                {esNumeroFormato(val) ? `₡ ${val}` : val}
              </div>
            </div>
          ))}
        </div>
      )}

      <div
        className="table-wrap"
        style={{
          maxHeight: '50vh',
          overflow: 'auto',
          border: '1px solid var(--border)',
          borderRadius: 10,
        }}
      >
        <table className="data" style={{ fontSize: 13 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--surface-solid, var(--surface))' }}>
            <tr>
              <th style={{ width: 36 }}>#</th>
              {columnas.map((c) => (
                <th key={c.key} style={{ whiteSpace: 'nowrap' }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filas.map((fila, i) => {
              const esTotal = esFilaTotal(fila);
              return (
                <tr
                  key={i}
                  style={esTotal ? {
                    background: 'var(--accent-soft)',
                    fontWeight: 700,
                  } : undefined}
                >
                  <td className="muted">{esTotal ? '' : i + 1}</td>
                  {columnas.map((c) => (
                    <td
                      key={c.key}
                      className={esMonto(c.key, fila[c.key]) ? 'mono text-right' : undefined}
                      style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={celda(fila[c.key])}
                    >
                      {celda(fila[c.key])}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {Array.isArray(resumen) && resumen.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h4 style={{ fontSize: 14, marginBottom: 8 }}>Resumen (base e impuestos)</h4>
          <div
            className="table-wrap"
            style={{
              maxHeight: '30vh',
              overflow: 'auto',
              border: '1px solid var(--border)',
              borderRadius: 10,
            }}
          >
            <table className="data" style={{ fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--surface-solid, var(--surface))' }}>
                <tr>
                  {Object.keys(resumen[0]).map((k) => (
                    <th key={k} style={{ whiteSpace: 'nowrap' }}>{labelResumen(k)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {resumen.map((fila, i) => (
                  <tr key={i}>
                    {Object.keys(resumen[0]).map((k) => (
                      <td
                        key={k}
                        className={esMonto(k, fila[k]) ? 'mono text-right' : undefined}
                      >
                        {celda(fila[k])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
        Los totales aparecen al final de la tabla y en el archivo Excel (CSV).
      </p>
    </div>
  );
}

function labelResumen(k) {
  const map = {
    seccion: 'Seccion',
    concepto: 'Concepto',
    documentos: 'Docs',
    subtotal: 'Base / Subtotal',
    iva: 'IVA',
    iva_acreditable: 'IVA acreditable',
    total: 'Total',
  };
  return map[k] || k;
}

function esFilaTotal(fila) {
  if (!fila) return false;
  if (fila._es_total === '1' || fila._es_total === 1 || fila._es_total === true) return true;
  const vals = Object.values(fila).map((v) => String(v || '').toUpperCase());
  return vals.some((v) =>
    v === 'TOTAL'
    || v.startsWith('TOTAL ')
    || v.includes('TOTAL DE ')
    || v.includes('POR PAGAR')
  );
}

function celda(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'object') {
    if (v instanceof Date) return fechaHora(v);
    return String(v);
  }
  return String(v);
}

function esNumeroFormato(value) {
  return typeof value === 'string' && /^-?\d+\.\d{2}$/.test(value);
}

function esMonto(key, value) {
  if (key && /monto|total|subtotal|iva|descuento|gravado|exento|costo|precio|valor|base/i.test(key)) {
    return true;
  }
  return esNumeroFormato(value);
}
